import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
	Plus,
	Search,
	Linkedin,
	Mail,
	Phone as PhoneIcon,
	Filter,
	Settings2,
	Loader2
} from 'lucide-react';
import {
	Badge,
	Button,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Textarea,
	useToast
} from '@hiring-intelligence/ui';
import {
	CONTACT_STATUS_OPTIONS,
	SENTIMENT_OPTIONS,
	CRM_MAIN_INDUSTRIES,
	CRM_INDUSTRY_GROUPS
} from '@hiring-intelligence/shared';
import { crmContactsApi, companyApi } from '@/services/api';
import {
	ListPageShell,
	FilterChipsBar,
	ListToolbarRow,
	CrmDataTable,
	NameCell,
	CrmCreateDrawer,
	FormField,
	type Column,
	type ChipDef
} from '@/components/crm/layout';

const PAGE_LIMIT = 25;
const STATUS_BADGE: Record<string, any> = { client: 'success', candidate: 'default', lead: 'secondary', partner: 'outline', other: 'secondary' };
const initials = (n?: string | null) => (n ? n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') : '?');

const SAVED_VIEWS: { chip: ChipDef; filters: { status?: string; sentiment?: string } }[] = [
	{ chip: { value: 'all', label: 'All contacts' }, filters: {} },
	{ chip: { value: 'opportunities', label: 'Open opportunities' }, filters: { status: 'lead' } },
	{ chip: { value: 'followup', label: 'Need follow up' }, filters: { sentiment: 'cold' } }
];

const emptyContact = {
	name: '', email: '', companyId: '', title: '', phone: '', whatsapp: '', linkedinUrl: '',
	status: '', sentiment: '', source: '', mainIndustry: '', subIndustry: '', country: '', notes: ''
};

export default function ContactsPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const [view, setView] = useState<string>('all');
	const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const [sentimentFilter, setSentimentFilter] = useState<string>('all');
	const [page, setPage] = useState(1);

	const [createOpen, setCreateOpen] = useState(false);
	const [draft, setDraft] = useState({ ...emptyContact });

	// Merge saved-view filters with the toolbar's adjustable filters; the toolbar wins when set.
	const activeViewFilters = useMemo(() => SAVED_VIEWS.find((v) => v.chip.value === view)?.filters || {}, [view]);
	const effectiveStatus = statusFilter !== 'all' ? statusFilter : activeViewFilters.status;
	const effectiveSentiment = sentimentFilter !== 'all' ? sentimentFilter : activeViewFilters.sentiment;

	const { data, isLoading } = useQuery({
		queryKey: ['crm-contacts', { page, q: searchQuery, status: effectiveStatus, sentiment: effectiveSentiment }],
		queryFn: () => crmContactsApi.list({
			page, limit: PAGE_LIMIT,
			q: searchQuery || undefined,
			status: effectiveStatus,
			sentiment: effectiveSentiment
		})
	});

	const { data: companiesData } = useQuery({
		queryKey: ['companies'],
		queryFn: () => companyApi.list(),
		refetchOnWindowFocus: false
	});

	const createMutation = useMutation({
		mutationFn: (d: typeof emptyContact) => crmContactsApi.create({
			name: d.name,
			email: d.email || undefined, companyId: d.companyId || undefined, title: d.title || undefined,
			phone: d.phone || undefined, whatsapp: d.whatsapp || undefined, linkedinUrl: d.linkedinUrl || undefined,
			status: d.status || undefined, sentiment: d.sentiment || undefined, source: d.source || undefined,
			mainIndustry: d.mainIndustry || undefined, subIndustry: d.subIndustry || undefined,
			country: d.country || undefined, notes: d.notes || undefined
		}),
		onSuccess: (_res, _vars, _ctx) => {
			queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
			toast({ title: 'Contact created' });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const submit = (andAddAnother: boolean) => {
		if (!draft.name.trim()) {
			toast({ title: 'Name required', variant: 'destructive' });
			return;
		}
		createMutation.mutate(draft, {
			onSuccess: () => {
				if (andAddAnother) {
					setDraft({ ...emptyContact });
				} else {
					setCreateOpen(false);
					setDraft({ ...emptyContact });
				}
			}
		});
	};

	const subIndustryOptions = draft.mainIndustry ? CRM_INDUSTRY_GROUPS[draft.mainIndustry] || [] : [];
	const contacts: any[] = data?.data || [];
	const meta = data?.meta;

	const columns: Column<any>[] = [
		{
			key: 'name',
			header: 'Name',
			cell: (c) => (
				<NameCell
					avatar={
						<span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
							{initials(c.name)}
						</span>
					}
					name={c.name}
					secondary={c.title}
				/>
			)
		},
		{
			key: 'company',
			header: 'Company Name',
			cell: (c) => c.company?.name ? (
				<a href={`/companies/${c.companyId}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">{c.company.name}</a>
			) : <span className="text-muted-foreground">—</span>
		},
		{
			key: 'email',
			header: 'Email',
			cell: (c) => c.email ? (
				<a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-primary hover:underline">
					<Mail className="h-3 w-3" /> {c.email}
				</a>
			) : <span className="text-muted-foreground">—</span>
		},
		{
			key: 'phone',
			header: 'Phone',
			cell: (c) => c.phone ? (
				<span className="inline-flex items-center gap-1 text-foreground"><PhoneIcon className="h-3 w-3 text-muted-foreground" /> {c.phone}</span>
			) : <span className="text-muted-foreground">—</span>
		},
		{
			key: 'linkedin',
			header: 'LinkedIn',
			cell: (c) => c.linkedinUrl ? (
				<a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-primary hover:underline">
					<Linkedin className="h-3 w-3" /> Profile
				</a>
			) : <span className="text-muted-foreground">—</span>
		},
		{
			key: 'status',
			header: 'Status',
			cell: (c) => c.status ? <Badge variant={STATUS_BADGE[c.status] || 'secondary'}>{CONTACT_STATUS_OPTIONS.find((o) => o.value === c.status)?.label}</Badge> : <span className="text-muted-foreground">—</span>
		},
		{
			key: 'owner',
			header: 'Contact Owner',
			cell: (c) => c.owner?.name ? (
				<span className="inline-flex items-center gap-1.5">
					<span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">{initials(c.owner.name)}</span>
					{c.owner.name}
				</span>
			) : <span className="text-muted-foreground">—</span>
		}
	];

	return (
		<>
			<ListPageShell
				title="Contacts"
				subtitle="Clients, candidates, leads and partners across your network."
				recordCount={meta?.total}
				primaryActions={
					<>
						<Button size="sm" onClick={() => setCreateOpen(true)}>
							<Plus className="mr-1.5 h-4 w-4" /> Create contact
						</Button>
					</>
				}
				chips={
					<FilterChipsBar
						chips={SAVED_VIEWS.map((v) => v.chip)}
						activeValue={view}
						onSelect={(v) => { setView(v); setPage(1); }}
						onAddView={() => toast({ title: 'Custom views coming soon' })}
						totalViewsLabel={`(${SAVED_VIEWS.length}/50)`}
						allViewsHref={undefined}
					/>
				}
				toolbar={
					<ListToolbarRow>
						<div className="relative w-full max-w-sm">
							<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
							<Input
								placeholder="Search contacts…"
								value={searchQuery}
								onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
								className="h-9 pl-8 text-sm"
							/>
						</div>
						<Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
							<SelectTrigger className="h-9 w-[160px] text-sm"><SelectValue placeholder="Status" /></SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All statuses</SelectItem>
								{CONTACT_STATUS_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
							</SelectContent>
						</Select>
						<Select value={sentimentFilter} onValueChange={(v) => { setSentimentFilter(v); setPage(1); }}>
							<SelectTrigger className="h-9 w-[150px] text-sm"><SelectValue placeholder="Sentiment" /></SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All sentiment</SelectItem>
								{SENTIMENT_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
							</SelectContent>
						</Select>
						<Button variant="outline" size="sm" disabled>
							<Filter className="mr-1.5 h-3.5 w-3.5" /> Advanced filters
						</Button>
						<div className="ml-auto flex items-center gap-2">
							<Button variant="ghost" size="sm" disabled className="text-xs">Export</Button>
							<Button variant="ghost" size="sm" disabled className="text-xs">
								<Settings2 className="mr-1 h-3.5 w-3.5" /> Edit columns
							</Button>
						</div>
					</ListToolbarRow>
				}
			>
				<CrmDataTable
					columns={columns}
					rows={contacts}
					loading={isLoading}
					onRowClick={(c) => navigate(`/contacts/${c.id}`)}
					empty={<div className="text-muted-foreground">No contacts match these filters. <button onClick={() => setCreateOpen(true)} className="text-primary hover:underline">Create one →</button></div>}
					footer={meta && meta.totalPages > 1 ? (
						<div className="flex items-center justify-between">
							<span>Page {page} of {meta.totalPages}</span>
							<div className="flex gap-2">
								<Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Prev</Button>
								<Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={!meta.hasMore}>Next</Button>
							</div>
						</div>
					) : undefined}
				/>
			</ListPageShell>

			<CrmCreateDrawer
				open={createOpen}
				onOpenChange={(o) => { setCreateOpen(o); if (!o) setDraft({ ...emptyContact }); }}
				title="Create contact"
				description="Add a new contact — capture work email for clients, personal email for candidates."
				onSubmit={() => submit(false)}
				onSubmitAndAddAnother={() => submit(true)}
				submitting={createMutation.isPending}
			>
				<FormField label="Email" hint="Required for portal access for clients">
					<Input type="email" placeholder="email@example.com" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} />
				</FormField>
				<FormField label="Name" required>
					<Input placeholder="Full name" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
				</FormField>
				<FormField label="Current Status">
					<Select value={draft.status || 'none'} onValueChange={(v) => setDraft((d) => ({ ...d, status: v === 'none' ? '' : v }))}>
						<SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
						<SelectContent>
							<SelectItem value="none">Not set</SelectItem>
							{CONTACT_STATUS_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
						</SelectContent>
					</Select>
				</FormField>
				<FormField label="Phone number">
					<Input value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} placeholder="+62 ..." />
				</FormField>
				<FormField label="WhatsApp Phone Number">
					<Input value={draft.whatsapp} onChange={(e) => setDraft((d) => ({ ...d, whatsapp: e.target.value }))} placeholder="+62 ..." />
				</FormField>
				<FormField label="Main Industry">
					<Select value={draft.mainIndustry || 'none'} onValueChange={(v) => setDraft((d) => ({ ...d, mainIndustry: v === 'none' ? '' : v, subIndustry: '' }))}>
						<SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
						<SelectContent>
							<SelectItem value="none">Not set</SelectItem>
							{CRM_MAIN_INDUSTRIES.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
						</SelectContent>
					</Select>
				</FormField>
				<FormField label="Sub-Industry">
					<Select value={draft.subIndustry || 'none'} onValueChange={(v) => setDraft((d) => ({ ...d, subIndustry: v === 'none' ? '' : v }))} disabled={!draft.mainIndustry}>
						<SelectTrigger><SelectValue placeholder={draft.mainIndustry ? 'Select sub-industry' : 'Pick main industry first'} /></SelectTrigger>
						<SelectContent>
							<SelectItem value="none">Not set</SelectItem>
							{subIndustryOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
						</SelectContent>
					</Select>
				</FormField>
				<FormField label="Company">
					<Select value={draft.companyId || 'none'} onValueChange={(v) => setDraft((d) => ({ ...d, companyId: v === 'none' ? '' : v }))}>
						<SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
						<SelectContent>
							<SelectItem value="none">No company</SelectItem>
							{companiesData?.data?.map((c: any) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
						</SelectContent>
					</Select>
				</FormField>
				<FormField label="Job title">
					<Input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} placeholder="e.g. Head of Talent" />
				</FormField>
				<FormField label="Country / Region">
					<Input value={draft.country} onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))} placeholder="e.g. Indonesia" />
				</FormField>
				<FormField label="LinkedIn account">
					<Input value={draft.linkedinUrl} onChange={(e) => setDraft((d) => ({ ...d, linkedinUrl: e.target.value }))} placeholder="https://linkedin.com/in/..." />
				</FormField>
				<FormField label="Source">
					<Input value={draft.source} onChange={(e) => setDraft((d) => ({ ...d, source: e.target.value }))} placeholder="e.g. Referral" />
				</FormField>
				<FormField label="Notes">
					<Textarea rows={3} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} placeholder="Anything noteworthy…" />
				</FormField>
			</CrmCreateDrawer>
		</>
	);
}
